import '@/components/ProfileHeader.css';
import { FileText, MessageCircle, ShieldCheck, MapPin } from 'lucide-react';
import { HiringPreferencesSummary } from '@/components/HiringPreferencePicker';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import SEO from '@/components/SEO';
import ProfileShareButton from '@/components/ProfileShareButton';
import { buildCollaboratorSlug } from '@/lib/collaboratorSlug';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const extractYouTubeId = (url = '') => {
  const patterns = [
    /(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([\w-]+)/,
    /youtube\.com\/embed\/([\w-]+)/,
    /youtube\.com\/v\/([\w-]+)/,
    /youtube\.com\/(?:shorts|live)\/([\w-]+)/,
  ];
  for (const pattern of patterns) {
    const match = String(url || '').match(pattern);
    if (match) return match[1];
  }
  return null;
};
const detectVideoPlatform = (url = '') => {
  try {
    const host = new URL(String(url || '').trim()).hostname.toLowerCase();
    if (host.includes('youtube.com') || host.includes('youtu.be')) return 'YouTube';
    if (host.includes('instagram.com')) return 'Instagram';
    if (host.includes('facebook.com') || host.includes('fb.watch')) return 'Facebook';
    if (host.includes('vimeo.com')) return 'Vimeo';
  } catch (error) {
    return 'Video';
  }
  return 'Video';
};
const renderFormattedInlineText = (text) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${part}-${index}`} className="font-extrabold text-slate-950">{part.slice(2, -2)}</strong>;
    }
    return <span key={`${part}-${index}`}>{part}</span>;
  });
};
const isStructuredBioLine = (line = '') => {
  const value = String(line || '').trim();
  if (!value) return false;
  if (/^#{1,6}\s+/.test(value)) return true;
  if (/^(\d+)[.)]\s+/.test(value)) return true;
  if (/^(?:[-*]|\u2022|\u00e2\u20ac\u00a2)\s+/.test(value)) return true;
  return false;
};
const normalizeBioSource = (source = '') => {
  const text = String(source || '')
    .replace(/\r/g, '')
    .replace(/(Services\s*&\s*Specializations\s*:)/gi, '\n$1')
    .replace(/(Why\s+Choose[^?]+\?)/gi, '\n$1')
    .replace(/\s+\u2022\s+/g, '\n• ')
    .replace(/\s+([✨🌟💫])\s+/g, '\n$1 ')
    .trim();
  if (!text) return '';

  const rawLines = text.split('\n').map((line) => line.replace(/\s+/g, ' ').trimEnd());
  const normalizedLines = [];
  let paragraphBuffer = [];
  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    const paragraph = paragraphBuffer.join(' ').replace(/\s+/g, ' ').replace(/\s+([,.;:!?])/g, '$1').trim();
    if (paragraph) normalizedLines.push(paragraph);
    paragraphBuffer = [];
  };

  rawLines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      if (normalizedLines[normalizedLines.length - 1] !== '') normalizedLines.push('');
      return;
    }
    if (isStructuredBioLine(line)) {
      flushParagraph();
      normalizedLines.push(line);
      return;
    }
    paragraphBuffer.push(line);
  });

  flushParagraph();
  return normalizedLines.join('\n').trim();
};
const FormattedBio = ({ text, fallback }) => {
  const source = normalizeBioSource(text || fallback || '');
  if (!source) return null;
  return (
    <div className="space-y-3 text-base leading-8 text-gray-700" dir="auto">
      {source.split(/\r?\n/).map((rawLine, index) => {
        const line = rawLine.trim();
        if (!line) return <div key={`space-${index}`} className="h-1" />;
        const heading = line.match(/^#{1,6}\s+(.+)$/);
        if (heading) {
          return <h3 key={`heading-${index}`} className="pt-2 text-xl font-extrabold text-slate-950">{renderFormattedInlineText(heading[1])}</h3>;
        }
        const numbered = line.match(/^(\d+)[.)]\s+(.+)$/);
        if (numbered) {
          return (
            <div key={`number-${index}`} className="flex gap-3">
              <span className="mt-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-orange-50 text-xs font-extrabold text-[#C45A00] ring-1 ring-orange-100">{numbered[1]}</span>
              <p className="min-w-0 flex-1">{renderFormattedInlineText(numbered[2])}</p>
            </div>
          );
        }
        const bullet = line.match(/^(?:[-*]|\u2022|\u00e2\u20ac\u00a2)\s+(.+)$/);
        if (bullet) {
          return (
            <div key={`bullet-${index}`} className="flex gap-3">
              <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-[#FF6F00]" />
              <p className="min-w-0 flex-1">{renderFormattedInlineText(bullet[1])}</p>
            </div>
          );
        }
        const inlineBullets = line.split(/\s+(?:\u2022|\u00e2\u20ac\u00a2)\s+/).map((part) => part.trim()).filter(Boolean);
        if (inlineBullets.length > 1) {
          return (
            <div key={`inline-bullets-${index}`} className="space-y-2">
              {inlineBullets[0] && (
                <p className={/:\s*$/.test(inlineBullets[0]) ? 'font-extrabold text-slate-950' : ''}>
                  {renderFormattedInlineText(inlineBullets[0])}
                </p>
              )}
              <div className="grid gap-2 sm:grid-cols-2">
                {inlineBullets.slice(1).map((item, itemIndex) => (
                  <div key={`${item}-${itemIndex}`} className="flex gap-3 rounded-2xl bg-orange-50/60 px-3 py-2">
                    <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-[#FF6F00]" />
                    <p className="min-w-0 flex-1 text-sm leading-6">{renderFormattedInlineText(item)}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return <p key={`line-${index}`}>{renderFormattedInlineText(line)}</p>;
      })}
    </div>
  );
};

const BIO_BULLET = String.fromCharCode(8226);
const BIO_BULLET_SPLIT_REGEX = /\s+(?:\u2022|\u00e2\u20ac\u00a2)\s+/g;
const BIO_BULLET_LINE_REGEX = /^(?:[-*]|\u2022|\u00e2\u20ac\u00a2)\s+/;

const normalizeCollaboratorBioSource = (source = '') => {
  const bulletLine = `\n${BIO_BULLET} `;
  const text = String(source || '')
    .replace(/\r/g, '')
    .replace(/(Services\s*&\s*Specializations\s*:)/gi, '\n### $1')
    .replace(/(Why\s+Choose[^?]+\?)/gi, '\n### $1')
    .replace(/(\s+)(Explore\s+[^.]+\s+on\s+KalaManch\.art)/gi, '\n$2')
    .replace(BIO_BULLET_SPLIT_REGEX, bulletLine)
    .trim();
  if (!text) return '';

  const rawLines = text.split('\n').map((line) => line.replace(/\s+/g, ' ').trim());
  const normalizedLines = [];
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    const paragraph = paragraphBuffer.join(' ').replace(/\s+/g, ' ').replace(/\s+([,.;:!?])/g, '$1').trim();
    if (paragraph) normalizedLines.push(paragraph);
    paragraphBuffer = [];
  };

  rawLines.forEach((line) => {
    if (!line) {
      flushParagraph();
      return;
    }
    if (/^#{1,6}\s+/.test(line) || BIO_BULLET_LINE_REGEX.test(line) || /^(\d+)[.)]\s+/.test(line)) {
      flushParagraph();
      normalizedLines.push(line);
      return;
    }
    paragraphBuffer.push(line);
  });

  flushParagraph();
  return normalizedLines.join('\n').trim();
};

const withCollaboratorLocationLine = (bio = '', location = '') => {
  const cleanBio = String(bio || '').replace(/\r/g, '').trim();
  const cleanLocation = String(location || '').trim();
  if (!cleanLocation) return cleanBio;
  const locationLine = `**📍 ${cleanLocation}**`;
  if (!cleanBio) return locationLine;
  const lines = cleanBio.split('\n');
  const locationIndex = lines.findIndex((line) => /^(?:\*\*)?📍/.test(line.trim()));
  if (locationIndex >= 0) {
    lines[locationIndex] = locationLine;
    return lines.join('\n').trim();
  }
  return `${locationLine}\n\n${cleanBio}`;
};

const CollaboratorFormattedBio = ({ text, fallback }) => {
  const source = normalizeCollaboratorBioSource(text || fallback || '');
  if (!source) return null;

  return (
    <div className="space-y-3 text-base leading-8 text-gray-700" dir="auto">
      {source.split(/\r?\n/).map((rawLine, index) => {
        const line = rawLine.trim();
        if (!line) return null;

        const heading = line.match(/^#{1,6}\s+(.+)$/);
        if (heading) {
          return (
            <h3 key={`heading-${index}`} className="pt-3 text-xl font-extrabold text-slate-950">
              {renderFormattedInlineText(heading[1].replace(/:\s*$/, ''))}
            </h3>
          );
        }

        const numbered = line.match(/^(\d+)[.)]\s+(.+)$/);
        if (numbered) {
          return (
            <div key={`number-${index}`} className="flex gap-3">
              <span className="mt-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-orange-50 text-xs font-extrabold text-[#C45A00] ring-1 ring-orange-100">{numbered[1]}</span>
              <p className="min-w-0 flex-1">{renderFormattedInlineText(numbered[2])}</p>
            </div>
          );
        }

        const bullet = line.match(/^(?:[-*]|\u2022|\u00e2\u20ac\u00a2)\s+(.+)$/);
        if (bullet) {
          return (
            <div key={`bullet-${index}`} className="flex gap-3">
              <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-[#FF6F00]" />
              <p className="min-w-0 flex-1">{renderFormattedInlineText(bullet[1])}</p>
            </div>
          );
        }

        return <p key={`line-${index}`}>{renderFormattedInlineText(line)}</p>;
      })}
    </div>
  );
};
const isEnabledFlag = (value, defaultValue = true) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'string') return value.toLowerCase() !== 'false';
  return Boolean(value);
};

const realLookingFor = (collaborator = {}) =>
  (Array.isArray(collaborator.looking_for) ? collaborator.looking_for : [])
    .map((item) => String(item).trim())
    .filter(Boolean);
const uniqueSeoItems = (items = [], limit = 34) => {
  const seen = new Set();
  return items
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .filter((item) => {
      const key = item.replace(/\s+/g, ' ').toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
};
const trimSeoDescription = (value = '', limit = 220) => {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).replace(/[,\s.-]+$/, '')}.`;
};

const CollaboratorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { artist: currentArtist } = useArtistAuth();
  const { collaborator: currentCollaborator } = useCollaboratorAuth();
  const [collaborator, setCollaborator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [enquirySubject, setEnquirySubject] = useState('');
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [enquiryStatus, setEnquiryStatus] = useState('');

  useEffect(() => {
    let mounted = true;
    const loadCollaborator = async () => {
      try {
        const response = await axios.get(`${API}/collaborators/${id}`);
        if (mounted) {
          setCollaborator(response.data);
          const canonicalSlug = response.data.slug || buildCollaboratorSlug(response.data);
          if (canonicalSlug && canonicalSlug !== id) {
            window.history.replaceState(null, '', `/collaborator/${canonicalSlug}`);
          }
        }
      } catch (err) {
        if (mounted) setError(err.response?.data?.detail || 'Collaborator not found');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadCollaborator();
    return () => {
      mounted = false;
    };
  }, [id, navigate]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/collaborators');
  };

  const handleSendEnquiry = async () => {
    if (!currentArtist) {
      setEnquiryStatus('Sign in as an artist to contact this collaborator.');
      return;
    }
    if (!currentArtist.is_verified) {
      setEnquiryStatus('Your artist profile must be completed and approved by admin before contacting collaborators.');
      return;
    }

    try {
      await axios.post(
        `${API}/artist-auth/enquiries`,
        {
          collaborator_id: collaborator.id,
          subject: enquirySubject,
          message: enquiryMessage,
        },
        { withCredentials: true },
      );
      setEnquiryStatus('Message sent. Continue the conversation from your artist inbox.');
      setShowEnquiryForm(false);
      setEnquirySubject('');
      setEnquiryMessage('');
    } catch (err) {
      try {
        const fallback = await axios.get(`${API}/artist-auth/enquiries`, { withCredentials: true });
        const conversations = fallback.data?.conversations || [];
        const matchingConversation = conversations.find((conversation) =>
          conversation.collaborator_id === collaborator.id &&
          String(conversation.subject || '').trim() === String(enquirySubject || '').trim()
        );

        if (matchingConversation) {
          setEnquiryStatus('Message sent. Continue the conversation from your artist inbox.');
          setShowEnquiryForm(false);
          setEnquirySubject('');
          setEnquiryMessage('');
          return;
        }
      } catch (fallbackError) {
        // Keep the original error message below if the fallback check also fails.
      }
      setEnquiryStatus(err.response?.data?.detail || 'Failed to send enquiry.');
    }
  };

  if (loading && !collaborator) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16 pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse rounded-3xl bg-white p-6 shadow-xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-center">
              <div className="h-28 w-28 rounded-full bg-teal-100" />
              <div className="flex-1 space-y-4">
                <div className="h-8 w-2/3 rounded-full bg-slate-100" />
                <div className="h-5 w-1/3 rounded-full bg-slate-100" />
                <div className="h-5 w-1/4 rounded-full bg-slate-100" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!collaborator) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 px-4 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-3">Collaborator not found</h1>
        <p className="text-gray-600 mb-6">{error}</p>
        <Link to="/collaborators" className="btn-primary">Back to Collaborators</Link>
      </div>
    );
  }

  const instagramHandle = collaborator.instagram?.replace(/^@/, '');
  const activeViewerRole = currentArtist ? 'artist' : currentCollaborator ? 'collaborator' : 'visitor';
  const isOwnProfile = activeViewerRole === 'collaborator' && currentCollaborator?.id === collaborator.id;
  const collaboratorAcceptsArtists = isEnabledFlag(collaborator.allow_artist_enquiries, true);
  const collaboratorAcceptsCollaborators = isEnabledFlag(collaborator.allow_collaborator_requests, true);
  const canViewContact = Boolean(isOwnProfile)
    || Boolean(activeViewerRole === 'artist' && currentArtist?.is_verified && collaboratorAcceptsArtists)
    || Boolean(activeViewerRole === 'collaborator' && currentCollaborator?.is_verified && !isOwnProfile && collaboratorAcceptsCollaborators);
  const collaboratorName = collaborator.organization_name || collaborator.name || 'Collaborator';
  const showContactPersonPublic = Boolean(collaborator.show_contact_person_public);
  const collaboratorMainCategory = String(collaborator.organization_category || '').trim();
  const collaboratorType = String(collaborator.display_profession || '').trim() || collaboratorMainCategory || collaborator.organization_type || 'creative collaborator';
  const collaboratorSubcategoryText = String(collaborator.organization_type || '').trim();
  const collaboratorCity = [collaborator.city, collaborator.state, collaborator.country || 'India']
    .map((value) => String(value || '').trim())
    .filter((value, index, values) => value && values.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index)
    .join(', ') || 'India';
  const publicCollaboratorBio = withCollaboratorLocationLine(collaborator.bio, collaboratorCity);
  const matchSuggestions = realLookingFor(collaborator);
  const collaboratorDescription = trimSeoDescription(
    `${collaboratorName} is a ${collaboratorType} from ${collaboratorCity} hiring and connecting with artists through Kalamanch.art. ${collaborator.bio || ''}`
  );
  const collaboratorKeywords = uniqueSeoItems([
    collaboratorName,
    collaboratorType,
    collaborator.organization_category,
    collaboratorSubcategoryText,
    ...(Array.isArray(collaborator.organization_subcategories) ? collaborator.organization_subcategories : []),
    ...matchSuggestions,
    collaboratorCity,
    `${collaboratorName} ${collaboratorType}`,
    `${collaboratorType} in ${collaboratorCity}`,
    `hire artists in ${collaboratorCity}`,
    `artist collaborator in ${collaboratorCity}`,
    'hire artists',
    'artist collaborator',
    'Kalamanch collaborator',
    'Kalamanch.art',
  ]).join(', ');
  const portfolioImages = Array.isArray(collaborator.portfolio_image_urls) ? collaborator.portfolio_image_urls.filter(Boolean) : [];
  const portfolioVideos = Array.isArray(collaborator.portfolio_video_urls) ? collaborator.portfolio_video_urls.filter(Boolean) : [];
  const collaboratorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: collaboratorName,
    description: collaborator.bio || collaboratorDescription,
    image: collaborator.image_url || undefined,
    address: collaboratorCity,
    url: window.location.href,
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <SEO
        title={`${collaboratorName} - ${collaboratorType}`}
        description={collaboratorDescription}
        keywords={collaboratorKeywords}
        image={collaborator.image_url}
        type="profile"
        schema={collaboratorSchema}
      />
      <div className="h-64 sm:h-72 bg-gradient-to-br from-[#00BFA5] via-[#4A148C] to-[#FF6F00] relative">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute top-24 sm:top-28 left-0 right-0 px-4 sm:px-8">
          <div className="max-w-7xl mx-auto">
            <button type="button" onClick={handleBack} className="inline-flex items-center rounded-full bg-white/20 px-4 py-2 text-sm sm:text-base text-white backdrop-blur-sm hover:bg-white/30 transition-colors shadow-sm">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Collaborators
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="profile-header bg-white rounded-3xl shadow-xl p-5 sm:p-8 mb-8">
          <div className="profile-header-layout">
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-white border border-orange-100 flex items-center justify-center text-4xl sm:text-5xl font-bold text-[#00BFA5] shrink-0">
              {collaborator.image_url ? (
                <img src={collaborator.image_url} alt={`${collaboratorName} ${collaboratorType} profile image on Kalamanch.art`} className="w-full h-full object-contain p-1" />
              ) : (
                (collaborator.organization_name || collaborator.name || 'C').charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 break-words">{collaboratorName}</h1>
              {showContactPersonPublic && collaborator.name && collaborator.name !== collaborator.organization_name && (
                <p className="mt-2 text-base font-semibold text-gray-700">Contact person: {collaborator.name}</p>
              )}
              <p className="text-lg sm:text-xl font-semibold text-[#00BFA5] mt-2">{collaboratorType}</p>
              {collaboratorSubcategoryText && collaboratorSubcategoryText !== collaboratorType && (
                <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">{collaboratorSubcategoryText}</p>
              )}
              <p className="profile-header-location"><MapPin aria-hidden="true" />{collaboratorCity}</p>
              {collaborator.is_verified && <span className="profile-header-verified"><ShieldCheck aria-hidden="true" />Verified collaborator</span>}
            </div>
            <div className="profile-header-actions">
              <ProfileShareButton
                title={`${collaboratorName} on Kalamanch.art`}
                text={`View ${collaboratorName}'s collaborator profile on Kalamanch.art`}
                className="w-full sm:w-auto"
              />
              {activeViewerRole === 'artist' && currentArtist?.is_verified && collaboratorAcceptsArtists && (
                <>
                  <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => setShowEnquiryForm((prev) => !prev)}>
                    Message Collaborator
                  </button>
                  <Link to="/artist/inbox" className="btn-outline w-full sm:w-auto">Open PMB</Link>
                </>
              )}
              {activeViewerRole === 'artist' && currentArtist && !currentArtist.is_verified && (
                <span className="rounded-full bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 border border-amber-200">Admin approval required to message</span>
              )}
              {activeViewerRole === 'artist' && currentArtist?.is_verified && !collaboratorAcceptsArtists && (
                <span className="rounded-full bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-500 border border-gray-200">Artist enquiries are turned off</span>
              )}
              {activeViewerRole === 'visitor' && (
                <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => window.dispatchEvent(new Event('openArtistJoin'))}>
                  <MessageCircle aria-hidden="true" />Sign In to Message
                </button>
              )}
              {activeViewerRole === 'collaborator' && currentCollaborator?.id === collaborator.id && (
                <Link to="/collaborator/account" className="btn-outline w-full sm:w-auto">Manage My Account</Link>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {showEnquiryForm && activeViewerRole === 'artist' && currentArtist?.is_verified && collaboratorAcceptsArtists && (
              <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
                <h2 className="text-2xl sm:text-3xl font-bold gradient-text mb-4">Send Enquiry</h2>
                <div className="space-y-4">
                  <input
                    value={enquirySubject}
                    onChange={(event) => setEnquirySubject(event.target.value)}
                    placeholder="Subject"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  />
                  <textarea
                    value={enquiryMessage}
                    onChange={(event) => setEnquiryMessage(event.target.value)}
                    rows="5"
                    placeholder="Tell the collaborator what kind of project, event, or performance you are interested in."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  />
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button type="button" className="btn-primary" onClick={handleSendEnquiry}>Send Enquiry</button>
                    <button type="button" className="btn-outline" onClick={() => setShowEnquiryForm(false)}>Cancel</button>
                  </div>
                  {enquiryStatus && (
                    <p className={`text-sm ${enquiryStatus.toLowerCase().includes('message sent') ? 'text-green-700' : 'text-red-600'}`}>
                      {enquiryStatus}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
              <h2 className="text-2xl sm:text-3xl font-bold gradient-text mb-5">About</h2>
              <CollaboratorFormattedBio text={publicCollaboratorBio} fallback="This collaborator is active on KalaManch.Art and looking to connect with artists." />
            </div>

            {matchSuggestions.length > 0 && (
            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Artist Needs</p>
                  <h2 className="mt-2 text-2xl sm:text-3xl font-bold gradient-text">
                    Looking for
                  </h2>
                </div>
                <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-[#C45A00]">Chosen by collaborator</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <HiringPreferencesSummary values={matchSuggestions} />
              </div>
              <p className="mt-4 text-sm text-gray-500">
                These preferences come directly from the collaborator profile.
              </p>
            </div>
            )}

            {(portfolioImages.length > 0 || portfolioVideos.length > 0) && (
              <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
                <h2 className="text-2xl sm:text-3xl font-bold gradient-text mb-5">Portfolio</h2>

                {portfolioImages.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6F00] mb-3">Photos</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {portfolioImages.map((imageUrl, index) => (
                        <a
                          key={`${imageUrl}-${index}`}
                          href={imageUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="group overflow-hidden rounded-2xl border border-orange-100 bg-gray-50"
                        >
                          <img
                            src={imageUrl}
                            alt={`${collaboratorName} portfolio ${index + 1}`}
                            loading="lazy"
                            className="h-40 w-full bg-white object-contain transition-transform duration-200 group-hover:scale-[1.02]"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {portfolioVideos.length > 0 && (
                  <div className={portfolioImages.length > 0 ? 'mt-6' : ''}>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6F00] mb-3">Videos</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {portfolioVideos.map((videoUrl, index) => {
                        const videoId = extractYouTubeId(videoUrl);
                        const platform = detectVideoPlatform(videoUrl);
                        return (
                          <div key={`${videoUrl}-${index}`} className="overflow-hidden rounded-2xl border border-purple-100 bg-white shadow-md">
                            <div className="aspect-video bg-gray-100">
                              {videoId ? (
                                <iframe
                                  width="100%"
                                  height="100%"
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={`${collaboratorName} portfolio video ${index + 1}`}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              ) : (
                                <a
                                  href={videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-gray-950 via-[#1f1235] to-[#FF6F00] px-5 text-center text-white"
                                >
                                  <span className="rounded-full bg-white/15 px-4 py-2 text-xs font-black uppercase tracking-[0.18em]">{platform}</span>
                                  <span className="text-lg font-black">Open portfolio video</span>
                                  <span className="max-w-xs text-xs text-white/75">This platform restricts website embeds, so the public video opens in a new tab.</span>
                                </a>
                              )}
                            </div>
                            <div className="bg-white p-4">
                              <h3 className="text-base font-semibold text-gray-800">Portfolio video {index + 1}</h3>
                              {!videoId && <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-[#FF6F00]">{platform}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
            <h2 className="text-2xl font-bold gradient-text mb-5">Contact</h2>
            {canViewContact ? (
              <div className="space-y-3 text-sm text-gray-700">
                {showContactPersonPublic && collaborator.name && collaborator.name !== collaborator.organization_name && (
                  <p><span className="font-semibold">Contact Person:</span> {collaborator.name}</p>
                )}
                {collaborator.email && <p><span className="font-semibold">Email:</span> {collaborator.email}</p>}
                {collaborator.mobile && <p><span className="font-semibold">Mobile:</span> {collaborator.mobile}</p>}
                {collaborator.website && <p><span className="font-semibold">Website:</span> <a href={collaborator.website} target="_blank" rel="noreferrer" className="text-[#00BFA5]">{collaborator.website}</a></p>}
                {instagramHandle && <p><span className="font-semibold">Instagram:</span> <a href={`https://instagram.com/${instagramHandle}`} target="_blank" rel="noreferrer" className="text-pink-600">@{instagramHandle}</a></p>}
              </div>
            ) : (
              <div className="rounded-2xl bg-orange-50 border border-orange-100 p-5">
                <p className="text-sm text-gray-700 mb-4">
                  Contact details and social links are visible only after login and admin approval to protect collaborators from scraping and spam.
                </p>
                <div className="flex flex-col gap-3">
                  {activeViewerRole === 'artist' && currentArtist && !currentArtist.is_verified ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      Your artist profile must be completed and approved by admin before collaborator contact links are visible.
                    </div>
                  ) : activeViewerRole === 'artist' && currentArtist && !collaboratorAcceptsArtists ? (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      This collaborator has turned off artist enquiries for now.
                    </div>
                  ) : activeViewerRole === 'collaborator' && currentCollaborator && !isOwnProfile ? (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      This collaborator has turned off collaborator-to-collaborator contact for now.
                    </div>
                  ) : (
                    <>
                      <button type="button" className="btn-primary w-full" onClick={() => window.dispatchEvent(new Event('openArtistJoin'))}>
                        Artist Sign In
                      </button>
                      <button type="button" className="btn-outline w-full" onClick={() => window.dispatchEvent(new Event('openCollaboratorJoin'))}>
                        Collaborator Sign In
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollaboratorProfile;
